import {assert} from 'Asserts';
import ArgType from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {EvaluablePrimitiveSpreadsheetFunction, Range} from 'notebook/tables/excess/FunctionConstants';
import {DivZeroError, NotApplicableError} from 'notebook/tables/excess/Util';
import toCartesianPoints from 'notebook/tables/excess/util/toCartesianPoints';
import {EvaluablePrimitive} from 'notebook/tables/typed-values/DataTypes';
import {validateData} from 'ui-library/charting/statistics/regression/LinearRegression';
import {mean} from 'ui-library/charting/statistics/StatUtils';
import {NumericCartesianPoint} from 'ui-library/charting/Types';

export default {
  name: 'RSQ',
  category: FunctionCategory.STATS,
  shortDescription: 'Returns the square of the Pearson correlation coefficient (R²).',
  description:
    'Returns the coefficient of determination (R²), which is the square of the Pearson product moment correlation coefficient through data points in known_y and known_x. This statistic represents how well the data points fit the model.',
  params: [
    {
      name: "known_y's",
      type: ArgType.cellRange,
      description: 'An array or cell range of numeric dependent data points.',
    },
    {
      name: "known_x's",
      type: ArgType.cellRange,
      description: 'An array or cell range of numeric independent data points.',
    },
  ],
  eval: (knownYs: Range<EvaluablePrimitive>, knownXs: Range<EvaluablePrimitive>): number => {
    const cartesianPoints = toCartesianPoints(knownYs, knownXs);
    return calculateRSquared(cartesianPoints);
  },
} satisfies EvaluablePrimitiveSpreadsheetFunction;

function calculateRSquared(points: Array<NumericCartesianPoint>): number {
  const dataError = validateData(points);
  if (dataError) {
    throw new NotApplicableError(dataError);
  }

  const n = points.length;
  assert(n >= 2, 'RSQ requires at least two data points.');

  const xValues = points.map((point) => point.x);
  const yValues = points.map((point) => point.y);

  const meanX = mean(xValues);
  const meanY = mean(yValues);

  // Calculate the sum of squares
  let ssXY = 0; // sum of products of x and y deviations
  let ssX = 0; // sum of squared x deviations
  let ssY = 0; // sum of squared y deviations

  for (let i = 0; i < n; i++) {
    const xDev = xValues[i] - meanX;
    const yDev = yValues[i] - meanY;

    ssXY += xDev * yDev;
    ssX += xDev * xDev;
    ssY += yDev * yDev;
  }

  if (ssX === 0 || ssY === 0) {
    throw new DivZeroError('Cannot calculate R² when all x values or all y values are equal.');
  }

  // Calculate R-squared (square of the Pearson correlation coefficient)
  return (ssXY * ssXY) / (ssX * ssY);
}
